import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiResponse } from '../models/auth.models';
import {
  AvailabilityResult,
  CreateAccountPayload,
  ResetPasswordPayload,
  ResetPasswordResult,
} from '../models/registration.models';
import { ApiConfigService } from './api-config.service';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root',
})
export class RegistrationService {
  constructor(
    private readonly http: HttpClient,
    private readonly apiConfig: ApiConfigService,
    private readonly configService: ConfigService,
  ) {}

  checkAvailability(username: string, niu: string): Observable<AvailabilityResult> {
    const url =
      this.apiConfig.buildUrl('INSCRIPTION', 'USER', 'FIND_USER_BY_PHONE') +
      `${this.configService.keycloak.realm}/${username}/${niu.toUpperCase()}`;

    return this.http
      .get<ApiResponse<unknown>>(url, { observe: 'response' })
      .pipe(
        map((response: HttpResponse<ApiResponse<unknown>>) =>
          this.mapAvailabilityResponse(response.body, response.status)
        ),
        catchError((error: HttpErrorResponse) =>
          of(this.mapAvailabilityResponse(error.error, error.status))
        )
      );
  }

  private mapAvailabilityResponse(
    body: ApiResponse<unknown> | null,
    httpStatus: number
  ): AvailabilityResult {
    const code = body?.code ?? httpStatus;
    if (code === 200) {
      return { available: true };
    }

    return {
      available: false,
      message:
        body?.message ?? 'Un utilisateur avec ce numéro ou ce NIU existe déjà',
    };
  }

  createAccount(payload: CreateAccountPayload): Observable<ApiResponse<unknown>> {
    const url = this.apiConfig.buildUrl('INSCRIPTION', 'ACCOUNT', 'CREATE_USER');
    return this.http.post<ApiResponse<unknown>>(url, payload);
  }

  resetPassword(payload: ResetPasswordPayload): Observable<ResetPasswordResult> {
    const url = this.apiConfig.buildUrl('INSCRIPTION', 'ACCOUNT', 'CHANGE_PASSWORD_USER');

    return this.http
      .post<ApiResponse<unknown>>(url, payload, { observe: 'response' })
      .pipe(
        map((response: HttpResponse<ApiResponse<unknown>>) => {
          if (response.status === 204) {
            return { status: 'SUCCESS' as const };
          }

          const body = response.body;
          return {
            status: (body?.status ?? 'ERROR') as ResetPasswordResult['status'],
            message: body?.message,
          };
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of({ status: 'USERNAME_NOT_EXIST' as const });
          }

          const body = error.error as ApiResponse<unknown> | null;
          return of({
            status: (body?.status ?? 'ERROR') as ResetPasswordResult['status'],
            message: body?.message,
          });
        })
      );
  }
}
